import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { Dialog } from "primereact/dialog";
import { Slider } from "primereact/slider";
import { SunburstChartProps } from "../../types/types";
import { BsCheckCircleFill, BsFillInfoCircleFill } from "react-icons/bs";

const LEVEL_DETAILS = [
  {
    tilgang: "Trin 0",
    fokus: "Ingen",
    effekt: "Ubevidst",
    color: "#efd3b3",
  },
  {
    tilgang: "Trin 1",
    fokus: "Lille",
    effekt: "Bevidst",
    color: "#f3b481",
  },
  {
    tilgang: "Trin 2",
    fokus: "Mellem",
    effekt: "Begrænset",
    color: "#ee8b4a",
  },
  {
    tilgang: "Trin 3",
    fokus: "Stor",
    effekt: "Neutral",
    color: "#e85c1f",
  },
  {
    tilgang: "Trin 4",
    fokus: "Altomfattende",
    effekt: "Positiv",
    color: "#c33f27",
  },
];

const SunburstChart: React.FC<SunburstChartProps> = ({
  data,
  width = 500,
  height = 500,
  onClickNode,
  onUpdateData,
}) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const [tooltipContent, setTooltipContent] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [dialogVisible, setDialogVisible] = useState(false);
  const [zoom, setZoom] = useState<number>(100);

  useEffect(() => {
    const radius = Math.min(width, height) / 2;
    const ringCount = 5;
    const sectorCount = data.length;
    const ringWidth = radius / (ringCount + 1);

    const colorScale = d3
      .scaleLinear<string>()
      .domain([0, 4])
      .range(["#fde0c5", "#d45b3c"]);

    const svgRoot = d3.select(ref.current);
    svgRoot.selectAll("g").remove();

    const svg = svgRoot
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("font", "8px sans-serif")
      .style("width", "100%")
      .style("height", "auto")
      .append("g")
      .attr(
        "transform",
        `translate(${width / 2},${height / 2}) scale(${zoom / 100})`
      );

    const arc = d3
      .arc<any>()
      .startAngle((d) => d.startAngle)
      .endAngle((d) => d.endAngle)
      .innerRadius((d) => d.innerRadius)
      .outerRadius((d) => d.outerRadius);

    const angleStep = (2 * Math.PI) / sectorCount;

    interface PathData {
      name: string;
      group: string;
      descriptions?: string[];
      step: number;
      index: number;
      filled: boolean;
      startAngle: number;
      endAngle: number;
      innerRadius: number;
      outerRadius: number;
    }

    const paths: PathData[] = [];

    data.forEach((d, i) => {
      for (let step = 0; step < ringCount; step++) {
        paths.push({
          name: d.name,
          group: d.group,
          descriptions: d.descriptions,
          step,
          index: i,
          filled: step < d.step,
          startAngle: i * angleStep,
          endAngle: (i + 1) * angleStep,
          innerRadius: step * ringWidth,
          outerRadius: (step + 1) * ringWidth,
        });
      }
    });

    svg
      .selectAll("path")
      .data(paths)
      .join("path")
      .attr("d", arc)
      .attr("fill", (d) => (d.filled ? colorScale(d.step) : "#f6f6f6"))
      .attr("stroke", "#fff")
      .on("mousemove", (event, d) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        setTooltipPos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
        setTooltipContent(
          d.descriptions?.[d.step] ?? `${d.name} – Trin ${d.step}`
        );
      })
      .on("mouseleave", () => {
        setTooltipContent(null);
        setTooltipPos(null);
      })
      .on("click", (event, d) => {
        const updated = [...data];
        updated[d.index] = {
          ...updated[d.index],
          step: d.step + 1,
        }
        //data[d.index].step = d.step + 1;
        console.log("Clicked node:", d.name, "Step:", d.step + 1);
        onUpdateData?.(updated);
      });

    // --- Curve theme labels (data names) along arcs ---
    const themeArcRadius = ringWidth * (ringCount + 0.15);
    data.forEach((d, i) => {
      const angle = (i + 0.5) * angleStep;
      const arcSweep = angleStep * 0.85; // slightly less than sector width
      const arcStart = angle - arcSweep / 2;
      const arcEnd = angle + arcSweep / 2;
      const r = themeArcRadius;

      let x1 = Math.sin(arcStart) * r;
      let y1 = -Math.cos(arcStart) * r;
      let x2 = Math.sin(arcEnd) * r;
      let y2 = -Math.cos(arcEnd) * r;

      const rotation = (angle * 180) / Math.PI;
      const isLowerHalf = rotation > 90 && rotation < 270;

      const pathId = `themeArcPath${i}`;

      let arcFlag = 0;
      let dPath;
      if (isLowerHalf) {
        // Move label further from center if on lower half
        const extraRadius = 0.08 * ringWidth;
        x1 = Math.sin(arcStart) * (r + extraRadius);
        y1 = -Math.cos(arcStart) * (r + extraRadius);
        x2 = Math.sin(arcEnd) * (r + extraRadius);
        y2 = -Math.cos(arcEnd) * (r + extraRadius);
        [x1, y1, x2, y2] = [x2, y2, x1, y1];
        arcFlag = 0;
        dPath = `M${x1},${y1} A${r},${r} 0 ${arcFlag} 0 ${x2},${y2}`;
      } else {
        arcFlag = 0;
        dPath = `M${x1},${y1} A${r},${r} 0 ${arcFlag} 1 ${x2},${y2}`;
      }

      svg.append("defs").append("path").attr("id", pathId).attr("d", dPath);

      svg
        .append("text")
        .attr("class", "theme")
        .style("font-size", "7px")
        .style("font-weight", "normal")
        .append("textPath")
        .attr("href", `#${pathId}`)
        .attr("startOffset", "50%")
        .attr("text-anchor", "middle")
        .attr("align-self", "left")
        .attr("cursor", "pointer")
        .text(d.name.length > 16 ? d.name.slice(0, 16) + "…" : d.name)
        .on("mousemove", (event) => {
          const rect = ref.current?.getBoundingClientRect();
          if (!rect) return;
          setTooltipPos({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
          });
          setTooltipContent(d.name);
        })
        .on("mouseleave", () => {
          setTooltipContent(null);
          setTooltipPos(null);
        });
    });

    const groupAngles = [0, 0.25, 0.5, 0.75].map((f) => f * 2 * Math.PI);
    const groupLabels = [
      "Kunderelationer",
      "Virksomhedsdrift",
      "Organisation",
      "Forretningskoncept",
    ];

    // --- Add curved group labels using textPath ---
    // Remove the old svg.selectAll("text.group") block and add this instead:
    const groupArcRadius = ringWidth * (ringCount + 0.7);
    const groupArcSweep = angleStep * 4 * 0.7; // covers a bit less than a full quadrant

    // Create group arc paths
    groupLabels.forEach((label, i) => {
      const angle = groupAngles[i] + angleStep * 2.5;
      const arcStart = angle - groupArcSweep / 2;
      const arcEnd = angle + groupArcSweep / 2;
      const r = groupArcRadius;

      // Calculate arc start/end points
      let x1 = Math.sin(arcStart) * r;
      let y1 = -Math.cos(arcStart) * r;
      let x2 = Math.sin(arcEnd) * r;
      let y2 = -Math.cos(arcEnd) * r;

      // Determine if label is on lower half (angle in degrees between 90 and 270)
      const angleDeg = ((angle * 180) / Math.PI) % 360;
      const isLowerHalf = angleDeg > 90 && angleDeg < 270;

      // Unique id for each path
      const pathId = `groupArcPath${i}`;

      // For lower half, reverse arc direction and sweep flag
      let arcFlag = 0;
      let d;
      if (isLowerHalf) {
        // Swap start/end and set sweep flag to 0 (draw arc the other way)
        [x1, y1, x2, y2] = [x2, y2, x1, y1];
        arcFlag = 0;
        d = `M${x1},${y1} A${r},${r} 0 ${arcFlag} 0 ${x2},${y2}`;
      } else {
        arcFlag = 0;
        d = `M${x1},${y1} A${r},${r} 0 ${arcFlag} 1 ${x2},${y2}`;
      }

      // Append path to svg defs
      svg.append("defs").append("path").attr("id", pathId).attr("d", d);

      // Append textPath for label
      svg
        .append("text")
        .attr("class", "group")
        .style("font-size", "11px")
        .style("font-weight", "bold")
        .append("textPath")
        .attr("href", `#${pathId}`)
        .attr("startOffset", "50%")
        .attr("text-anchor", "middle")
        .text(label);
    });
  }, [data, width, height, onClickNode, onUpdateData, zoom]);

  return (
    <div style={{ position: "relative" }}>
      <BsFillInfoCircleFill
        color="#1F2D40"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          fontSize: "2.5rem",
          cursor: "pointer",
        }}
        onClick={() => setDialogVisible(true)}
      />

      <i
        className="pi pi-info-circle"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          fontSize: "2.5rem",
          cursor: "pointer",
          color: "var(--secondary-color)",
        }}
        onClick={() => setDialogVisible(true)}
      />
      <Dialog
        header="Trinoversigt for bæredygtighed"
        visible={dialogVisible}
        style={{ width: "40rem" }}
        onHide={() => setDialogVisible(false)}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>
                Tilgang til bæredygtighed
              </th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>
                Fokus på bæredygtighed
              </th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>
                Effekter og indvirkninger
              </th>
            </tr>
          </thead>
          <tbody>
            {LEVEL_DETAILS.map((level, index) => (
              <tr key={index} style={{ backgroundColor: level.color }}>
                <td style={{ padding: "0.5rem" }}>{level.tilgang}</td>
                <td style={{ padding: "0.5rem" }}>{level.fokus}</td>
                <td style={{ padding: "0.5rem" }}>{level.effekt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Dialog>

      <svg ref={ref}></svg>

      {/* Zoom slider in lower right */}
      <div
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          width: 180,
          zIndex: 1000,
          background: "rgba(255,255,255,0.9)",
          borderRadius: 8,
          padding: "8px 12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span style={{ marginRight: 8, fontSize: 12 }}>Zoom</span>
        <Slider
          value={zoom}
          min={25}
          max={250}
          step={1}
          onChange={(e) => setZoom(e.value as number)}
          style={{ flex: 1, marginRight: 8 }}
        />
        <span style={{ width: 38, textAlign: "right", fontSize: 12 }}>
          {zoom}%
        </span>
      </div>

      {tooltipContent && tooltipPos && (
        <div
          style={{
            position: "absolute",
            top: tooltipPos.y,
            left: tooltipPos.x,
            background: "white",
            border: "1px solid #ccc",
            padding: "6px 10px",
            fontSize: "18px",
            pointerEvents: "none",
            zIndex: 9999,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px",
            transform: "translate(10px, 10px)",
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};

export default SunburstChart;
